/**
 * Pave NIP (design 4.2, maquette nip.html) : 3 x 4 touches de 96 x 96 px,
 * 14 px d'espace, points de saisie. Les points tremblent sur une erreur
 * (300 ms, rien si prefers-reduced-motion). Le NIP saisi n'est jamais
 * affiche : seulement un point par chiffre.
 */
import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { palette } from './theme';

export const PIN_MIN_LENGTH = 4;
export const PIN_MAX_LENGTH = 6;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-10px); }
  40%, 80% { transform: translateX(10px); }
`;

const KEY_SX = {
    width: 96, height: 96, minWidth: 96, minHeight: 96, fontSize: 34, fontWeight: 800, borderRadius: 2,
};

export function PinDots({ length, shakeKey }) {
    const count = Math.max(PIN_MIN_LENGTH, length);
    return (
        <Box
            key={shakeKey}
            data-testid="nip-points"
            data-saisis={length}
            aria-label={`${length} chiffre${length > 1 ? 's' : ''} saisi${length > 1 ? 's' : ''}`}
            role="img"
            sx={{
                display: 'flex', gap: '18px', my: 2,
                animation: shakeKey ? `${shake} 300ms ease-in-out` : 'none',
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
        >
            {Array.from({ length: count }, (_, i) => (
                <Box
                    key={i}
                    sx={{
                        width: 22, height: 22, borderRadius: '50%', border: `3px solid ${palette.primary}`,
                        bgcolor: i < length ? palette.primary : 'transparent',
                    }}
                />
            ))}
        </Box>
    );
}

export default function PinPad({ length, disabled, onDigit, onBackspace, onSubmit }) {
    const canSubmit = !disabled && length >= PIN_MIN_LENGTH;
    const digit = (d) => (
        <Button
            key={d}
            variant="outlined"
            disabled={disabled || length >= PIN_MAX_LENGTH}
            onClick={() => onDigit(String(d))}
            sx={{ ...KEY_SX, color: palette.text, borderColor: palette.border, bgcolor: palette.surface }}
        >
            {d}
        </Button>
    );
    return (
        <Box
            role="group"
            aria-label="Pavé numérique"
            sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 96px)', gap: '14px' }}
        >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit)}
            <Button
                variant="outlined"
                aria-label="Effacer"
                disabled={disabled || length === 0}
                onClick={onBackspace}
                sx={{ ...KEY_SX, color: palette.text, borderColor: palette.border, bgcolor: palette.surface }}
            >
                <BackspaceOutlinedIcon sx={{ fontSize: 34 }} />
            </Button>
            {digit(0)}
            <Button
                variant="contained"
                aria-label="Valider"
                disabled={!canSubmit}
                onClick={onSubmit}
                sx={KEY_SX}
            >
                <CheckIcon sx={{ fontSize: 38 }} />
            </Button>
        </Box>
    );
}
